import type { FarmingActivity } from '../page';

interface ActivityListProps {
  activities: FarmingActivity[];
  onEdit: (activity: FarmingActivity) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: FarmingActivity['status']) => void;
}

export default function ActivityList({ activities, onEdit, onDelete, onStatusChange }: ActivityListProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Scheduled': return 'bg-blue-100 text-blue-700';
      case 'In Progress': return 'bg-orange-100 text-orange-700';
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'Delayed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-700';
      case 'High': return 'bg-orange-100 text-orange-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'Low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'Planting': return 'ri-seedling-line';
      case 'Harvesting': return 'ri-scissors-cut-line';
      case 'Irrigation': return 'ri-drop-line';
      case 'Fertilization': return 'ri-flask-line';
      case 'Pest Control': return 'ri-bug-line';
      case 'Soil Management': return 'ri-landscape-line';
      case 'Maintenance': return 'ri-tools-line';
      case 'Land Preparation': return 'ri-shovel-line';
      default: return 'ri-plant-line';
    }
  };

  if (activities.length === 0) {
    return (
      <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
        <i className="ri-plant-line text-6xl text-gray-300 mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Activities Found</h3>
        <p className="text-sm text-gray-600">Start by adding your first farming activity</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity) => (
        <div key={activity.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4 flex-1">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getPriorityColor(activity.priority)}`}>
                <i className={`${getActivityIcon(activity.activityType)} text-2xl w-6 h-6 flex items-center justify-center`}></i>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{activity.activityName}</h3>
                <p className="text-sm text-gray-600 mb-3">{activity.description}</p>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                    {activity.status}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(activity.priority)}`}>
                    {activity.priority} Priority
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                    {activity.activityType}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    {activity.cropType}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onEdit(activity)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                title="Edit"
              >
                <i className="ri-edit-line text-lg text-gray-600 w-5 h-5 flex items-center justify-center"></i>
              </button>
              <button
                onClick={() => onDelete(activity.id)}
                className="p-2 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                title="Delete"
              >
                <i className="ri-delete-bin-line text-lg text-red-600 w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Location</p>
              <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                <i className="ri-map-pin-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                {activity.location}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Assigned To</p>
              <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                <i className="ri-user-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                {activity.assignedTo}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Scheduled Date</p>
              <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                <i className="ri-calendar-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                {new Date(activity.scheduledDate).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Estimated Cost</p>
              <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                <i className="ri-money-rupee-circle-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                ₹{activity.estimatedCost.toLocaleString()}
              </p>
            </div>
          </div>

          {activity.resources && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-1">Resources</p>
              <p className="text-sm text-gray-700">{activity.resources}</p>
            </div>
          )}

          {activity.completionDate && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Completion Date</p>
                <p className="text-sm font-medium text-green-600">
                  {new Date(activity.completionDate).toLocaleDateString()}
                </p>
              </div>
              {activity.actualCost && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Actual Cost</p>
                  <p className="text-sm font-medium text-gray-900">₹{activity.actualCost.toLocaleString()}</p>
                </div>
              )}
            </div>
          )}

          {activity.notes && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Notes</p>
              <p className="text-sm text-gray-700">{activity.notes}</p>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Created on {new Date(activity.createdDate).toLocaleDateString()}
              </p>
              <select
                value={activity.status}
                onChange={(e) => onStatusChange(activity.id, e.target.value as FarmingActivity['status'])}
                className="px-3 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="Scheduled">Scheduled</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Delayed">Delayed</option>
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
